/**
 * Data hooks for Admin Setup > Email Templates.
 *
 * The two lists (notification types, scheduled report emails) load once per
 * mount through {@link useFetchData}. The preview is keyed by the selected row
 * and ignores any response that arrives after the selection has moved on —
 * clicking two templates in quick succession must never leave the first one's
 * HTML showing under the second one's name.
 */
import { useEffect, useRef, useState } from 'react';
import {
  emailQueueAPI,
  type EmailPreviewBranding,
  type EmailTemplateType,
  type ScheduledReportEmail,
  type User,
} from '../../../../lib/api';
import { useView } from '../../../../contexts/ViewContext';
import { getErrorMessage, useFetchData, type FetchDataResult } from '../../../../hooks/useFetchData';

/**
 * Who may open Email Templates — mirrors the backend IsEmailTemplateViewer:
 * Helix staff, or an admin of a customer company. Supplier admins are
 * excluded even though they are company admins.
 *
 * @param user - The signed-in user, or null.
 * @returns True when the user may see the section and its previews.
 */
export function canViewEmailTemplates(user: User | null | undefined): boolean {
  return Boolean(
    (user?.company_type === 'helix' && (user?.is_staff || user?.is_superuser))
    || (user?.company_type === 'customer' && user?.is_company_admin),
  );
}

/** What the preview frame renders, whichever kind of email it is. */
export interface EmailPreview {
  subject: string;
  html: string;
  /** Notification previews only: whose branding was applied. */
  branding?: EmailPreviewBranding;
  /** Scheduled report previews only: files that ride along as attachments. */
  attachments?: string[];
}

export interface UseEmailPreviewResult {
  preview: EmailPreview | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * A key identifying the selected tenant, which changes only once the company
 * switcher has finished *saving* that selection.
 *
 * CR-7725: both lists below are tenant-specific now, so they have to follow the
 * switcher — loaded once per visit, switching customer left the previous
 * customer's templates and count on screen until the page was reloaded.
 *
 * But the server reads the selected tenant off the user's profile, and the
 * switcher persists it with a PATCH. Refetching the instant the selection
 * changes therefore races that PATCH and can answer for the *previous*
 * customer — the same staleness, now intermittent and much harder to see.
 * Waiting for `isSaving` to clear makes the refetch fire once, after the
 * server knows which customer it is being asked about. ViewContext states the
 * ordering requirement explicitly: "the DB must be updated BEFORE
 * data-fetching API calls fire".
 *
 * @returns An opaque key safe to use as a refetch dependency.
 */
function useSettledTenantKey(): string {
  const {
    viewLevel, selectedGroupId, selectedCompanyId, selectedSiteId, isSaving,
  } = useView();
  const current = `${viewLevel}:${selectedGroupId}:${selectedCompanyId}:${selectedSiteId}`;
  // Seeded with the current value so a first render does not fetch twice.
  const [settled, setSettled] = useState(current);
  // CR-7725: every completed view save refetches, even when the key looks the
  // same — re-picking "All Companies" after a stale site was left on the
  // profile changes what the server answers without changing this key.
  const [saves, setSaves] = useState(0);
  const wasSaving = useRef(false);

  useEffect(() => {
    if (isSaving) {
      wasSaving.current = true;
      return;
    }
    if (wasSaving.current) {
      wasSaving.current = false;
      setSaves((n) => n + 1);
    }
    setSettled(current);
  }, [isSaving, current]);

  return `${settled}#${saves}`;
}

/**
 * Notification types grouped by category, for the selected tenant.
 *
 * @returns useFetchData result; `data` maps category name to its types.
 */
export function useNotificationTemplateTypes(): FetchDataResult<Record<string, EmailTemplateType[]>> {
  const tenantKey = useSettledTenantKey();
  return useFetchData(async () => {
    const res = await emailQueueAPI.listTemplateTypes();
    return res.data && typeof res.data === 'object' ? res.data : {};
  }, { deps: [tenantKey] });
}

/**
 * The scheduled report emails the viewer may see (own company; Helix staff: all).
 *
 * @returns useFetchData result; `data` is the full, unpaginated list.
 */
export function useScheduledReportEmails(): FetchDataResult<ScheduledReportEmail[]> {
  // Scoped to the viewer's company server-side, so it follows the switcher for
  // the same reason the list above does.
  const tenantKey = useSettledTenantKey();
  return useFetchData(async () => {
    const res = await emailQueueAPI.listScheduledReportEmails();
    return Array.isArray(res.data) ? res.data : [];
  }, { deps: [tenantKey] });
}

/**
 * Load a preview for `key`, dropping stale responses.
 *
 * @param key - Stable identity of the selection (e.g. `notification:po_created`), or null for none.
 * @param load - Fetches the preview for that selection.
 * @returns The current preview, its loading/error state and a reload callback.
 * @example
 *   const { preview, loading, error } = useEmailPreview(
 *     selected ? `report:${selected}` : null,
 *     () => loadReportPreview(selected),
 *   );
 */
export function useEmailPreview(
  key: string | null,
  load: () => Promise<EmailPreview>,
): UseEmailPreviewResult {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setPreview(null);
    setError(null);
    if (!key) {
      setLoading(false);
      return undefined;
    }
    let current = true;
    setLoading(true);
    load()
      .then((result) => {
        if (current) setPreview(result);
      })
      .catch((err: unknown) => {
        if (current) setError(getErrorMessage(err, 'Could not load this preview'));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
    // `load` is rebuilt every render by the caller; `key` is what identifies
    // the request, and `attempt` is the explicit retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  return { preview, loading, error, reload: () => setAttempt((n) => n + 1) };
}

/** Fetch a notification template preview. */
export async function loadNotificationPreview(type: string): Promise<EmailPreview> {
  const res = await emailQueueAPI.previewTemplate(type);
  return { subject: res.data.subject, html: res.data.html, branding: res.data.branding };
}

/** Fetch a scheduled report email preview. */
export async function loadScheduledReportPreview(id: number): Promise<EmailPreview> {
  const res = await emailQueueAPI.previewScheduledReportEmail(id);
  return { subject: res.data.subject, html: res.data.html, attachments: res.data.attachments };
}
